import { Col, Row, Form } from "react-bootstrap";
import RefreshIcon from "@mui/icons-material/Refresh";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import {
  TextField,
  Checkbox,
  Grid,
  FormControlLabel,
  Tooltip,
  InputAdornment,
  ListItem,
} from "@mui/material";
import "./Groups.css";
import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
  createGroups,
  getAccounts,
  getGroupsDataEdit,
  getRoles,
} from "../../api/groupsApi/groupsApi";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { useLoader } from "../../context/LoaderContext";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import LocalStorageUtil from "../../utils/localStorage";

function not(a: readonly number[], b: readonly number[]) {
  return a.filter((value) => !b.includes(value));
}

function intersection(a: readonly number[], b: any) {
  return b.filter((role: any) => a.includes(role.roleId));
}

interface GroupActionsProps {
  businessUnitData: any;
}

const GroupActions: React.FC<GroupActionsProps> = ({ businessUnitData }) => {
  const location = useLocation();
  const { rowData, action } = location.state || {};
  const [searchTerm, setSearchTerm] = useState("");
  const [history, setHistory] = useState<
    Array<{
      left: readonly number[];
      right: readonly number[];
      checked: readonly number[];
    }>
  >([]);
  const [data, setData] = useState<any>([]);
  const [checked, setChecked] = React.useState<readonly number[]>([]);
  const [left, setLeft] = useState<readonly number[]>([]);
  const [accountData, setAccountData] = useState<readonly number[]>([]);
  const [right, setRight] = useState<any>([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(rowData?.active);
  const [formData, setFormData] = useState({
    accountName: data[0]?.accountName || "",
    groupName: data[0]?.groupName || "",
    groupType: data[0]?.groupType || "",
    contextName: data[0]?.contextName || "",
  });

  const [formErrors, setFormErrors] = useState({
    groupName: "",
    groupType: "",
  });

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);
  const { setLoading } = useLoader();

  const handleToggle = (value: number) => () => {
    saveToHistory();
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];
    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  const handleAllRight = () => {
    setRight(right.concat(left));
    setLeft([]);
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleAllLeft = () => {
    saveToHistory();
    setLeft(left.concat(right));
    setRight([]);
  };

  const saveToHistory = () => {
    setHistory((prev) => [...prev, { left, right, checked }]);
  };

  const undoLastAction = () => {
    if (history.length > 0) {
      const lastState = history[history.length - 1];
      setLeft(lastState.left);
      setRight(lastState.right);
      setChecked(lastState.checked);
      setHistory((prev) => prev.slice(0, -1));
    }
  };

  const customList = (items: readonly number[]) => (
    <Paper sx={{ width: "21.5em", height: 490, overflow: "auto" }}>
      <List dense component="div" role="ul">
        {items.length === 0 ? (
          <ListItemText sx={{ textAlign: "center", color: "gray" }}>
            No items available...
          </ListItemText>
        ) : (
          items.map((value: any) => {
            const isSelected = checked.indexOf(value.roleId) !== -1;
            return (
              <ListItem component="li" key={value?.groupId}>
                <ListItemButton
                  onClick={handleToggle(value.roleId)}
                  sx={{
                    backgroundColor: isSelected
                      ? " rgb(77, 123, 167)"
                      : "transparent",
                    border: isSelected ? "1px solid darkblue" : "",
                    color: isSelected ? "white" : "default",
                    "&:hover": {
                      backgroundColor: isSelected ? "darkblue" : "lightgray",
                    },
                  }}
                >
                  <ListItemText primary={value.roleName} />
                </ListItemButton>
              </ListItem>
            );
          })
        )}
      </List>
    </Paper>
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (name === "groupName" || name === "groupType") {
      validateText(value, name);
    }
  };

  const handleDropdownChange = (event: any) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (name === "contextName") {
      getAllRoles(value);
    }
  };

  useEffect(() => {
    if (data && data.length > 0) {
      setFormData({
        accountName: data[0]?.accountName || "",
        groupName: data[0]?.groupName || "",
        groupType: data[0]?.groupType || "",
        contextName: data[0]?.contextName || "",
      });
      if (action === "Duplicate") {
        setFormData({
          accountName: data[0]?.accountName || "",
          groupName: "",
          groupType: data[0]?.groupType || "",
          contextName: data[0]?.contextName || "",
        });
      }
    }
  }, [data]);

  const groupId = rowData ? rowData.id : 0;
  const getData = async () => {
    setLoading(true);
    const payload = {
      contextName:
        businessUnitData?.contextName ||
        LocalStorageUtil.getItem("contextName"),
      GroupDataModelObj: { GroupId: groupId },
    };
    try {
      const response = await getGroupsDataEdit(payload);
      setData(response);
      if (response) {
        getAllRoles(LocalStorageUtil.getItem("contextName"), response);
      }
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  const getAllRoles = async (contextName: any, rightResponse?: any) => {
    setLoading(true);
    let payload = {
      RoleDataModelObj: {},
      contextName: contextName,
    };
    try {
      const response = await getRoles(payload);
      setLeft(response);
      if (action === "Edit" || action === "Duplicate") {
        const leftRoleIds = new Set(
          rightResponse[0]?.role.map((role: any) => role.roleId)
        );
        const removedItems = response.filter((item: any) =>
          leftRoleIds.has(item.roleId)
        );
        const updatedLeft = response.filter(
          (item: any) => !leftRoleIds.has(item.roleId)
        );
        setLeft(updatedLeft);
        setRight(removedItems);
      }
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  const getAllAccountsName = async () => {
    setLoading(true);
    const payload = {
      contextName: LocalStorageUtil.getItem("contextName"),
    };
    try {
      const data = await getAccounts(payload);
      setAccountData(data);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getAllAccountsName();
  }, [left]);

  useEffect(() => {
    getData();
  }, [groupId]);

  const validateText = (text: string, fieldName: keyof typeof formErrors) => {
    const textPattern = /^[a-zA-Z\s]+$/;
    if (!textPattern.exec(text)) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "Please enter alphabets only",
      }));
    } else {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "",
      }));
    }
  };
  const handleConfirmDialogClose = (confirm: boolean) => {
    try {
      setConfirmDialogOpen(false);
      if (confirm) {
        navigate("/groupview");
      } else {
        setConfirmDialogOpen(false);
      }
    } catch (error) {
      console.error("Error in handleConfirmDialogClose", error);
    }
  };

  const handelCancel = () => {
    setConfirmDialogOpen(true);
  };

  const handleCreate = async () => {
    const groupIds = (right || []).map((g: any) => g.roleId);
    const GroupId = (() => {
      switch (action) {
        case "Edit":
          return rowData?.id;
        case "Duplicate":
          return 0;
        default:
          return null;
      }
    })();
    const payload = {
      contextName: LocalStorageUtil.getItem("contextName"),
      GroupModelObj: {
        GroupName: formData.groupName,
        GroupType: formData.groupType,
        BusinessUnitId: Number(LocalStorageUtil.getItem("businessUnitId")),
        Active: action === "Edit" || action === "Duplicate" ? isActive : true,
        RoleId: JSON.stringify(groupIds),
        CreatedBy: LocalStorageUtil.getItem("userId"),
        CreatedDate: rowData?.createdDate ? rowData?.createdDate : null,
        GroupId: GroupId,
      },
    };
    try {
      await createGroups(payload);
      navigate("/groupview");
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleStatusChange = () => {
    setIsActive((prevIsActive: any) => {
      const updatedStatus = !prevIsActive;
      return updatedStatus;
    });
  };

  return (
    <div style={{overflow: "hidden"}}>
      {action === "Edit" ? (
        <NavigationBar
          menuTitle="Configuration"
           title="User Management"
          screenTitle="Groups"
          text="Group - Update Group"
        />
      ) : (
        <NavigationBar
          menuTitle="Configuration"
           title="User Management"
          screenTitle="Groups"
          text="Group - Create New Group"
        />
      )}
      <div className="ag-theme-quartz-dark" style={{ marginLeft: "4vh" }}>
        <div
          className="col-sm-5"
          style={{
            width: "97%",
            textAlign: "right",
            margin: "10px",
          }}
        >
          {action === "Edit" && (
            <Tooltip
              title={
                rowData.active
                  ? "Click to make the group inactive"
                  : "Click to make the group active"
              }
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isActive}
                    onChange={handleStatusChange}
                    color="primary"
                    size="small"
                  />
                }
                label={isActive ? "Active" : "In-Active"}
                style={{
                  margin: "3px 3px",
                  textTransform: "none",
                  fontSize: "8px",
                  color: isActive ? "#154360" : "#7B241C",
                  backgroundColor: isActive ? "#D6EAF8" : "#FADBD8",
                  padding: "3px 6px",
                  borderRadius: "3px",
                  alignItems: "center",
                  height: "30px",
                }}
              />
            </Tooltip>
          )}
        </div>
        <h4
          className="form-heading"
          style={{ color: "var(--aggrid-text-color)", fontSize: "12px" }}
        >
          BASIC INFORMATION
        </h4>
        <Form>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group controlId="formCustomer">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Customer<span className="required">*</span>
                </Form.Label>
                <Form.Select
                  name="contextName"
                  disabled={action === "Edit" || action === "Duplicate"}
                  value={formData.contextName}
                  onChange={handleDropdownChange}
                  className="select-dropdown"
                  style={{ fontSize: "11px" }}
                >
                  {action !== "Edit" && action !== "Duplicate" && (
                    <option value="">Select customer</option>
                  )}{" "}
                  {accountData.map((account: any) => (
                    <option key={account.id} value={account.contextName}>
                      {account.accountName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="formGroupName">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Group Name<span className="required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter group name"
                  name="groupName"
                  value={formData.groupName}
                  onChange={handleChange}
                  required
                  isInvalid={!!formErrors.groupName}
                  className="input-box"
                  style={{ fontSize: "11px" }}
                />
                {formErrors.groupName && (
                  <Form.Control.Feedback type="invalid">
                    {formErrors.groupName}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="formGroupType">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Group Type<span className="required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter group type"
                  name="groupType"
                  value={formData.groupType}
                  onChange={handleChange}
                  required
                  isInvalid={!!formErrors.groupType}
                  className="input-box"
                  style={{ fontSize: "11px" }}
                />
                {formErrors.groupType && (
                  <Form.Control.Feedback type="invalid">
                    {formErrors.groupType}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Label
                className="input-labels"
                style={{ color: "var(--aggrid-text-color)", fontSize: "11px" }}
              >
                Roles<span className="required">*</span>
              </Form.Label>
              <TextField
                variant="outlined"
                placeholder="Search..."
                fullWidth
                onChange={handleSearchChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: "white",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    height: "30px",
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: "#ccc",
                    },
                    "&:hover fieldset": {
                      borderColor: "#888",
                    },
                    "& input": {
                      fontSize: "11px",
                    },
                  },
                }}
              />
            </Col>
          </Row>
          <Grid
            container
            spacing={2}
            sx={{
              justifyContent: "left",
              alignItems: "left",
              fontSize: "11px",
            }}
          >
            <Grid item>
              {customList(
                left.filter((item: any) =>
                  item.roleName.toLowerCase().includes(searchTerm.toLowerCase())
                )
              )}
            </Grid>
            <Grid item>
              <Grid container direction="column" sx={{ alignItems: "center" }}>
                <Button
                  sx={{ my: 0.5, height: "25px" }}
                  variant="outlined"
                  size="small"
                  onClick={handleAllRight}
                  disabled={left.length === 0}
                  aria-label="move all right"
                  className="arrow-button"
                >
                  ≫
                </Button>
                <Button
                  sx={{ my: 0.5, height: "25px" }}
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedRight}
                  disabled={leftChecked.length === 0}
                  aria-label="move selected right"
                  className="arrow-button"
                >
                  &gt;
                </Button>
                <Button
                  sx={{ my: 0.5, height: "25px" }}
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedLeft}
                  disabled={rightChecked.length === 0}
                  aria-label="move selected left"
                  className="arrow-button"
                >
                  &lt;
                </Button>
                <Button
                  sx={{ my: 0.5, height: "25px" }}
                  variant="outlined"
                  size="small"
                  onClick={handleAllLeft}
                  disabled={right.length === 0}
                  aria-label="move all left"
                  className="arrow-button"
                >
                  ≪
                </Button>
                <Button
                  sx={{ my: 0.5, height: "25px" }}
                  variant="outlined"
                  size="small"
                  onClick={undoLastAction}
                  disabled={history.length === 0}
                  aria-label="undo last action"
                  className="arrow-button"
                >
                  <RefreshIcon
                    fontSize="small"
                    sx={{
                      fontSize: "14px",
                      height: "14px",
                      width: "14px",
                    }}
                  />
                </Button>
              </Grid>
            </Grid>
            <Grid item>{customList(right)}</Grid>
          </Grid>
        </Form>

        <Grid
          item
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
            marginLeft: "-1vh",
            
          }}
        >
          {action === "Edit" ? (
            <Button
              variant="contained"
              className="update-button"
              onClick={handleCreate}
              style={{
                fontSize: "11px",
                borderRadius: "4px",
                border: "1px solid #858c94",
                height: "25px",
              }}
            >
              Update
            </Button>
          ) : (
            <Button
              variant="contained"
              className="update-button"
              onClick={handleCreate}
            >
              Create
            </Button>
          )}
          <Button
            variant="contained"
            className="cancel-button"
            onClick={handelCancel}
          >
            Cancel
          </Button>
        </Grid>
        <ConfirmDialog
          open={confirmDialogOpen}
          onClose={handleConfirmDialogClose}
          title={`Groups -  ${
            action === "Edit" ? "Update Group" : "Create Group"
          }`}
          content="Are you sure you want to Cancel?"
        />
      </div>
    </div>
  );
};

export default GroupActions;
